<script setup>
import { ref, defineProps, defineEmits, onMounted, computed } from "vue";
import { apiQuery } from "@/api/j24api";
import DeviceView from "./DeviceView.vue";
import AppViewCommands from "./AppViewCommands.vue";
const props = defineProps({
  app: Object,
  apps: Array,
  devices: Array,
  license: Object,
  commands: Array,
});
const emit = defineEmits(["back", "UpdateCommands"]);
const selectedDevice = ref(null);
const selectedTab = ref("App");
const searchDevices = ref("");

const randomKeys = ref({
  deviceView: 0,
});

function updateRandom(key) {
  randomKeys.value[key] = Math.floor(Math.random() * 100);
}

function selectDevice(device, row) {
  selectedDevice.value = row.item;
  selectedTab.value = "Device";
}

function getAppBundleId(id) {
  if (!id)
    return props.app.unsupportedApp ? props.app.CFBundleIdentifier : "Unknown";
  console.log("Getting bundle ID for app:", id);
  let match = props.apps.find((a) => a.id == id);
  if (match.bundleId) {
    return match.bundleId;
  }
  return null;
}

function getLatestVersion(id, silicon) {
  let match = props.apps.find((a) => {
    if (silicon) {
      return a.id == id && a.stream == "AppleSilicon";
    }
    if (!silicon) {
      return a.id == id && a.stream == "Intel";
    }
  });
  console.log(id, silicon);
  console.log("Match", match);
  if (match && match.latestApprovedPatch) {
    return match.latestApprovedPatch.version;
  }
}

function runningLatestVersion() {
  let latestVersion = props.app.latestVersion;
  var devicesOnLatest = props.app.devices.filter(
    (d) => d.version == latestVersion || d.status == "Newer version"
  );
  console.log("Devices on latest version", devicesOnLatest.length);
  if (devicesOnLatest.length != 0) {
    return ((devicesOnLatest.length / props.app.devices.length) * 100).toFixed(
      1
    );
  }
  return 0;
}

// function to compare the device version with the latest version
// and return whether it is up to date or newer or older versions are semantic
// in varying formats 1.1 to 1.1.1.1.1
function compareVersions(version1, version2) {
  let v1 = version1.split(".");
  let v2 = version2.split(".");
  for (let i = 0; i < Math.max(v1.length, v2.length); i++) {
    let num1 = parseInt(v1[i] || "0");
    let num2 = parseInt(v2[i] || "0");
    if (num1 > num2) {
      return -1;
    } else if (num1 < num2) {
      return 1;
    }
  }
  return 0;
}

function updateStatus(device) {
  var result = device;
  let versionCompare = compareVersions(device.version, props.app.latestVersion);
  if (versionCompare == 0) {
    result.status = "Up to date";
  } else if (versionCompare == -1) {
    result.status = "Newer version";
  } else {
    result.status = "Requires update";
    if (device.updatesAvailable) {
      let match = device.updatesAvailable.find(
        (update) => update.appID == props.app.id
      );
      if (match) {
        if (match.status == "ReadyToInstall") {
          result.status = "Ready to install";
        }
      }
    }
  }
  return result;
}

const devicesList = computed(() => {
  let result = [];
  props.app.devices.forEach((device) => {
    // Find matching device in devices array
    let match = props.devices.find(
      (d) => d.Serial_Number == device.serialNumber
    );
    if (match) {
      let newDevice = Object.assign(device, match);

      result.push(updateStatus(newDevice));
    } else {
      result.push(device);
    }
  });
  return result;
});

let headers = computed(() => {
  let result = [];
  result.push(
    { title: "Device Name", value: "Computer_Name", sortable: true },
    { title: "Serial Number", value: "Serial_Number", sortable: true },
    { title: "Username", value: "username", sortable: true }
  );

  if (!props.app.unsupportedApp) {
    result.push({ title: "Compliant", value: "compliant", sortable: true });
  }

  result.push(
    {
      title: "Last Patch24 Contact",
      value: "patchLastContact",
      sortable: true,
    },
    { title: "Version Installed", value: "version", sortable: true }
    // { title: "App Path", value: "path" }
  );
  if (!props.app.unsupportedApp) {
    result.push({ title: "Status", value: "status" });
  }

  return result;
});

let pieChartConfig = {
  chart: {
    type: "radialBar",
  },
  colors: ["#e7541e"],
  // labels: ["Devices Online"],
  legend: {
    show: false,
    display: false,
  },
  plotOptions: {
    radialBar: {
      hollow: {
        size: "55%",
        margin: 200,
      },
      track: {
        show: true,
        strokeWidth: "100%",
      },
      dataLabels: {
        name: {
          show: false,
        },
        value: {
          show: true,
          offsetY: 6,
          fontWeight: "bold",
          fontSize: "22px",
        },
      },
    },
  },
  grid: {
    show: false,
    // padding: {
    //   top: -20,
    //   right: 0,
    //   bottom: -20,
    //   left: 0,
    // },
  },
};

function relativeTimeFromNow(date) {
  if (!date) return "";
  const now = new Date();
  const then = new Date(date);
  const diffMs = now - then;
  const diffSec = Math.floor(diffMs / 1000);

  if (diffSec < 60) return `${diffSec} seconds ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} minutes ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hours ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay <= 7) return `${diffDay} days ago`;

  return then.toLocaleString();
}

function getApprovedDate() {
  try {
    let match = props.apps.find((a) => a.id == props.app.id);
    if (match) {
      let approvedDate = match.latestApprovedPatch.approvedAt;
      return new Date(approvedDate).toLocaleDateString();
    }
  } catch (error) {
    return "Unknown";
  }
}
</script>

<template>
  <v-window v-model="selectedTab">
    <v-window-item value="App">
      <div style="display: flex; align-items: center" class="mb-2">
        <BreadcrumbEmit label="App Management" @click="emit('back')" />
        <BreadcrumbSeperator />
        <BreadcrumbEmit :label="props.app.application" notLink />
      </div>
      <h1>{{ props.app.application }}</h1>
      <br />
      <v-row>
        <v-col cols="auto" class="d-flex">
          <v-card class="d-flex align-center" height="285">
            <v-table class="ma-0 pa-0">
              <tbody>
                <tr>
                  <td class="font-weight-bold">Application Identifier</td>
                  <td>{{ getAppBundleId(app.id) }}</td>
                </tr>
                <template v-if="!props.app.unsupportedApp">
                  <tr>
                    <td class="font-weight-bold">
                      Latest Version (Apple Silicon)
                    </td>
                    <td>{{ getLatestVersion(app.id, true) }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold">Latest Version (Intel)</td>
                    <td>{{ getLatestVersion(app.id, false) }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold">Approved Date</td>
                    <td>{{ getApprovedDate() }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold">Minimum Compliant Version</td>
                    <td>{{ app.minimumCompliantVersion }}</td>
                  </tr>
                </template>
              </tbody>
            </v-table>
          </v-card>
        </v-col>
        <v-col cols="auto" v-if="!props.app.unsupportedApp">
          <v-card class="flex" height="285" width="285">
            <v-card-title class="font-weight-bold">Latest Version</v-card-title>
            <apexchart
              class="ma-0 pa-0"
              :options="pieChartConfig"
              :series="[runningLatestVersion()]"
            />
          </v-card>
        </v-col>
        <v-col cols="auto" v-if="!props.app.unsupportedApp">
          <v-card class="flex" height="285" width="285">
            <v-card-title class="font-weight-bold">
              <v-row>
                <v-col cols="auto"> Compliant Version </v-col>
                <v-spacer></v-spacer>
                <v-col>
                  <v-icon
                    size="small"
                    class="font-weight-medium"
                    color="grey-lighten-1"
                    >mdi-help-circle-outline</v-icon
                  >
                  <v-tooltip activator="parent" location="bottom">
                    Running the latest version or still within the update
                    <p></p>
                    deadline period set by the Patch Policy
                  </v-tooltip>
                </v-col>
              </v-row>
            </v-card-title>
            <apexchart
              class="ma-0 pa-0"
              :options="pieChartConfig"
              :series="[
                props.app.compliancePercentage
                  ? props.app.compliancePercentage
                  : 0,
              ]"
            />
          </v-card>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="4">
          <v-text-field
            placeholder="Search Devices..."
            density="compact"
            hide-details
            v-model="searchDevices"
            variant="solo"
          ></v-text-field>
        </v-col>
        <v-spacer></v-spacer>

        <v-col cols="auto" v-if="!props.app.unsupportedApp">
          <AppViewCommands
            :apps="props.apps"
            :app="props.app"
            :license="props.license"
            :deviceCommands="props.commands"
            :devices="props.devices"
            @UpdateCommands="emit('UpdateCommands')"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-data-table
            :headers="headers"
            :items="devicesList"
            @click:row="selectDevice"
            hover
            :search="searchDevices"
          >
            <template v-slot:[`item.compliant`]="{ item }">
              <v-btn
                rounded="xl"
                :color="item.compliant ? 'success' : 'error'"
                readonly
                size="small"
                >{{ item.compliant ? "Yes" : "No" }}</v-btn
              >
            </template>

            <template v-slot:[`item.patchLastContact`]="{ item }">
              <span>{{ relativeTimeFromNow(item.patchLastContact) }}</span>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-window-item>

    <v-window-item value="Device">
      <div style="display: flex; align-items: center" class="mb-2">
        <BreadcrumbEmit label="App Management" @click="emit('back')" />
        <BreadcrumbSeperator />
        <BreadcrumbEmit
          :label="props.app.application"
          @click="
            selectedTab = 'App';
            updateRandom('deviceView');
          "
        />
        <BreadcrumbSeperator />
        <BreadcrumbEmit :label="selectedDevice.Computer_Name" notLink />
      </div>
      <DeviceView
        :key="randomKeys.deviceView"
        :device="selectedDevice"
        :licenseId="license._id"
      />
    </v-window-item>
  </v-window>
</template>
